import { useQuery } from '@tanstack/react-query';
import { useWorkflowRunService } from '../services/workflowRunService';
import type { WorkflowRunDetail, WorkflowRunSummary } from '../../../types/backend';

export const customerWorkflowRunKeys = {
  all: (customerId?: string) => ['workflow-runs', customerId, 'customer'] as const,
  run: (customerId?: string, runId?: string) =>
    ['workflow-runs', customerId, 'customer', runId] as const,
};

/**
 * Every workflow run walked for a customer, whichever investigation shows it and including the
 * ones none does.
 *
 * Deliberately a separate key from the sidebar's `['workflow-runs', customerId, investigationId]`.
 * They answer different questions: the sidebar asks what this case is walking, this asks what has
 * been walked for the customer. Sharing a key would let one overwrite the other with a set that
 * looks the same and means something else.
 */
export const useCustomerWorkflowRuns = (customerId: string | undefined) => {
  const service = useWorkflowRunService();

  const query = useQuery<WorkflowRunSummary[]>({
    queryKey: customerWorkflowRunKeys.all(customerId),
    queryFn: () => service.listCustomerRuns(customerId as string),
    enabled: Boolean(customerId),
  });

  return {
    runs: query.data ?? [],
    loading: query.isLoading,
    // The message the server gave, so a refusal reads as itself rather than as an empty customer.
    // A listing that could not load is an unknown, and an empty state would report it as a
    // measured zero (PRIN-06).
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Could not load this customer’s workflow runs'
      : null,
    refetch: query.refetch,
  };
};

/**
 * One run read from the customer alone, for the customer-wide list where no investigation is in
 * hand. Separate from the sidebar's detail query for the reason the list is: the sidebar's key
 * carries an investigation id, and this run may have none.
 */
export const useCustomerWorkflowRun = (
  customerId: string | undefined,
  runId: string | null,
) => {
  const service = useWorkflowRunService();

  const query = useQuery<WorkflowRunDetail>({
    queryKey: customerWorkflowRunKeys.run(customerId, runId ?? undefined),
    queryFn: () => service.getCustomerRun(customerId as string, runId as string),
    enabled: Boolean(customerId && runId),
  });

  return {
    detail: runId ? (query.data ?? null) : null,
    loading: Boolean(runId) && query.isLoading,
    // Same reason as the listing: a detail that could not load says so, rather than presenting
    // an empty run (PRIN-06).
    error:
      runId && query.error
        ? query.error instanceof Error
          ? query.error.message
          : 'Could not load that workflow run'
        : null,
  };
};
